// CandidatePersonalInfo_new.jsx migration (Stage 3.9 language-resource rollout). Keys ordered to match
// the file's own reading order: page heading, Post Details card, Personal Information card (name
// fields, Aadhaar, DOB/Gender/Marital Status, Mobile/Email/Driving Licence), Address Details card,
// PwBD (Persons with Benchmark Disability) Details card (category selection, scribe sub-panels,
// disability-certificate upload, declaration checkbox), Cancel/Save buttons, then the ~19 validate()
// error messages in the order they're set.
//
// Entries marked "AI-authored, needs linguistic QA" had no existing Marathi anywhere in this file;
// the Marathi authored for those reuses this same file's own existing terminology wherever a matching
// term already appears elsewhere in the file (e.g. "कृपया...प्रविष्ट करा"/"कृपया...निवडा" for
// Please Enter/Select, "वैध" for Valid, "आवश्यक" for Required — the last two both already appear
// verbatim in this file's own "Is Scribe Required? / लेखनिक आवश्यक आहे का?" label and the scribe
// declaration paragraph's "अचूक" for accurate/exact). All other entries are extractions of text that
// was already bilingual in the file — zero new translation authored for those.
export default {
  // Page heading + card headers — all extracted (already bilingual in the file).
  pageHeading: { en: 'Candidate Personal Information', mr: 'उमेदवाराची वैयक्तिक माहिती' },
  postDetailsHeading: { en: 'Post Details', mr: 'पदाचे तपशील' },
  personalInformationHeading: { en: 'Personal Information', mr: 'वैयक्तिक माहिती' },
  addressDetailsHeading: { en: 'Address Details', mr: 'पत्ता तपशील' },
  pwbdDetailsHeading: {
    en: 'Persons with Benchmark Disability (PwBD) Details',
    mr: 'बेंचमार्क अपंगत्व असलेल्या व्यक्तींचा तपशील'
  },

  // Post Details card. appliedForLabel extracted (already bilingual). selectPostPlaceholder was
  // English-only ("Select Post") — AI-authored, needs linguistic QA.
  selectPostPlaceholder: { en: 'Select Post', mr: 'पद निवडा' },
  appliedForLabel: { en: 'Applied For', mr: 'पदा साठी अर्ज केला' },

  // Personal Information card — name fields + Aadhaar. All extracted (already bilingual).
  firstNameLabel: { en: 'First Name', mr: 'पहिले नाव' },
  middleNameLabel: { en: 'Middle Name', mr: 'वडिलांचे / पतीचे नाव' },
  lastNameLabel: { en: 'Last Name', mr: 'आडनाव' },
  motherNameLabel: { en: 'Mother Name', mr: 'आईचे नाव' },
  fatherNameLabel: { en: 'Father Name', mr: 'वडिलांचे नाव' },
  aadhaarNumberLabel: { en: 'Aadhaar Number', mr: 'आधार क्रमांक' },

  // DOB / Gender / Marital Status labels + radio option labels. All extracted (already bilingual).
  // The " Male"/" Female"/" Other"/" Married"/" Unmarried" leading space in the original JSX (for
  // visual gap next to the radio button) is preserved at the JSX call site, not baked into these
  // dictionary values.
  dateOfBirthLabel: { en: 'Date Of Birth', mr: 'जन्मतारीख' },
  genderLabel: { en: 'Gender', mr: 'लिंग' },
  maleLabel: { en: 'Male', mr: 'पुरुष' },
  femaleLabel: { en: 'Female', mr: 'महिला' },
  otherLabel: { en: 'Other', mr: 'इतर' },
  maritalStatusLabel: { en: 'Marital Status', mr: 'वैवाहिक स्थिती' },
  marriedLabel: { en: 'Married', mr: 'विवाहित' },
  unmarriedLabel: { en: 'Unmarried', mr: 'अविवाहित' },

  // Mobile / Email / Driving Licence. All extracted (already bilingual).
  mobileNoLabel: { en: 'Mobile No.', mr: 'मोबाइल क्रमांक' },
  emailIdLabel: { en: 'Email ID', mr: 'ईमेल आयडी' },
  drivingLicenceLabel: { en: 'Driving Licence No.', mr: 'ड्रायव्हिंग लायसन्स' },

  // Address Details card. All field labels extracted (already bilingual). selectStatePlaceholder was
  // English-only ("Select State") — AI-authored, needs linguistic QA, reuses this file's own "राज्य".
  addressLabel: { en: 'Address', mr: 'पत्ता' },
  villageLabel: { en: 'Village/City', mr: 'गाव/शहर' },
  talukaLabel: { en: 'Taluka', mr: 'तालुका' },
  districtLabel: { en: 'District', mr: 'जिल्हा' },
  selectStatePlaceholder: { en: 'Select State', mr: 'राज्य निवडा' },
  stateLabel: { en: 'State', mr: 'राज्य' },
  pinCodeLabel: { en: 'PIN Code', mr: 'पिन कोड' },

  // PwBD Details card. selectDisabilityCategoryCheckboxLabel and disabilityCategoryLabel extracted
  // (already bilingual, including the literal parentheses already present around the Marathi text in
  // the checkbox label's source — kept verbatim, not a delimiter added by this migration).
  // selectCategoryPlaceholder was English-only ("Select Category") — AI-authored, needs linguistic QA,
  // reuses this file's own "प्रकार" (from "अपंगत्व प्रकार").
  selectDisabilityCategoryCheckboxLabel: {
    en: 'Select your disability category',
    mr: '(अपंगत्व प्रकार निवडा)'
  },
  selectCategoryPlaceholder: { en: 'Select Category', mr: 'प्रकार निवडा' },
  disabilityCategoryLabel: { en: 'Disability Category', mr: 'अपंगत्व प्रकार निवडा' },
  isScribeRequiredLabel: { en: 'Is Scribe Required?', mr: 'लेखनिक आवश्यक आहे का?' },

  // Upload Disability Certificate sub-heading + upload button text — same bilingual phrase used twice
  // in the file for two different elements (a section h5 and an upload-button label); both extracted
  // (already bilingual), kept as separate keys since they're separate call sites.
  uploadDisabilityCertificateHeading: {
    en: 'Upload Disability Certificate',
    mr: 'अपंगत्व प्रमाणपत्र अपलोड करा'
  },
  uploadDisabilityCertificateButton: {
    en: 'Upload Disability Certificate',
    mr: 'अपंगत्व प्रमाणपत्र अपलोड करा'
  },

  // Scribe declaration checkbox text — extracted (already bilingual). The trailing
  // <span className="color-red">*</span> that sat mid-string (after the English sentence, before the
  // Marathi sentence) in the original JSX is rendered as a separate sibling element after <Text>,
  // matching this file's own established convention elsewhere (e.g. Date Of Birth/Gender/Marital
  // Status: the "*" is always a sibling span after the bilingual label, never embedded inside the
  // translated text) — no new translation authored, only the asterisk's position relative to the
  // (now single, concatenated) bilingual string shifts from mid-text to end-of-text.
  scribeDeclarationText: {
    en: 'I have read and understood all the guidelines, rules, and regulations regarding the use of a scribe. I hereby declare that the information provided by me is true and correct to the best of my knowledge. I understand that any discrepancy or misuse may result in the cancellation of my candidature.',
    mr: 'मी लेखनिक वापरासंदर्भातील सर्व मार्गदर्शक सूचना, नियम व अटी वाचल्या असून मला त्या पूर्णपणे समजल्या आहेत. मी येथे घोषित करतो/करते की माझ्याकडून दिलेली माहिती खरी आणि अचूक आहे. कोणताही विसंगती किंवा चुकीचा वापर झाल्यास माझा अर्ज रद्द केला जाऊ शकतो याची मला जाणीव आहे.'
  },

  // Form action buttons. Both extracted (already bilingual).
  cancelButton: { en: 'Cancel', mr: 'रद्द करा' },
  saveButton: { en: 'Save', mr: 'जतन करा' },

  // validate() error messages — all English-only in the file today. AI-authored, needs linguistic QA.
  // Marathi reuses this file's own field-name terminology (from the headings above) plus the
  // "कृपया...प्रविष्ट करा"/"कृपया...निवडा" (Please Enter/Select) and "वैध" (Valid) pattern already
  // established in modules/i18n/dictionary.js's login/passwordCommon/forgotPassword namespaces.
  appliedForError: { en: 'Please Select Applied Post', mr: 'कृपया अर्ज केलेले पद निवडा' },
  firstNameRequiredError: { en: 'Please Enter Valid First Name', mr: 'कृपया वैध पहिले नाव प्रविष्ट करा' },
  middleNameRequiredError: {
    en: 'Please Enter Valid Middle Name',
    mr: 'कृपया वैध वडिलांचे / पतीचे नाव प्रविष्ट करा'
  },
  lastNameRequiredError: { en: 'Please Enter Valid Last Name', mr: 'कृपया वैध आडनाव प्रविष्ट करा' },
  motherNameRequiredError: { en: 'Please Enter Valid Mother Name', mr: 'कृपया वैध आईचे नाव प्रविष्ट करा' },
  fatherNameRequiredError: {
    en: 'Please Enter Valid Father Name',
    mr: 'कृपया वैध वडिलांचे नाव प्रविष्ट करा'
  },
  dobRequiredError: { en: 'Please Select Valid Date of Birth', mr: 'कृपया वैध जन्मतारीख निवडा' },
  genderRequiredError: { en: 'Please Select Gender', mr: 'कृपया लिंग निवडा' },
  maritalStatusRequiredError: { en: 'Please Select Marital Status', mr: 'कृपया वैवाहिक स्थिती निवडा' },
  mobileRequiredError: { en: 'Please Enter Mobile number', mr: 'कृपया मोबाइल क्रमांक प्रविष्ट करा' },
  mobileInvalidError: {
    en: 'Mobile number must be exactly 10 digits',
    mr: 'मोबाइल क्रमांक अचूक १० अंकी असणे आवश्यक आहे'
  },
  emailRequiredError: { en: 'Please Enter Email', mr: 'कृपया ईमेल आयडी प्रविष्ट करा' },
  emailInvalidError: { en: 'Please Enter Valid email', mr: 'कृपया वैध ईमेल आयडी प्रविष्ट करा' },
  drivingLicenceRequiredError: {
    en: 'Please Enter Valid Driving Licence Number',
    mr: 'कृपया वैध ड्रायव्हिंग लायसन्स क्रमांक प्रविष्ट करा'
  },
  addressRequiredError: { en: 'Please Enter Address', mr: 'कृपया पत्ता प्रविष्ट करा' },
  villageRequiredError: { en: 'Please Enter Village', mr: 'कृपया गाव/शहर प्रविष्ट करा' },
  talukaRequiredError: { en: 'Please Enter Taluka', mr: 'कृपया तालुका प्रविष्ट करा' },
  stateRequiredError: { en: 'Please Enter State', mr: 'कृपया राज्य प्रविष्ट करा' },
  districtRequiredError: { en: 'Please Enter District', mr: 'कृपया जिल्हा प्रविष्ट करा' },
  pinRequiredError: { en: 'Please Enter Pin Code', mr: 'कृपया पिन कोड प्रविष्ट करा' },

  // Phase C.5 (Candidate System UI Language Migration): modalForPdf.jsx's ScribePdfModalViwer button —
  // the trigger that opens the (untouched, out-of-scope-per-C.5) embedded Scribe_Usage_Guidelines.pdf
  // viewer. Only this button/modal chrome is system UI in scope; the PDF itself is not translated.
  // mr is an extraction (the file's original Marathi text, with its embedded English "(Scribe Usage
  // Guidelines)" aside removed now that this key carries its own separate en value); en is AI-authored,
  // needs linguistic QA. The modal's "Close" button reuses homeSummary.closeButtonLabel rather than
  // duplicating an equivalent existing key.
  scribeGuidelinesButton: { en: 'Click Here for Scribe Usage Guidelines', mr: 'लेखनिक वापर मार्गदर्शक करिता येथे क्लिक करावे' }
};
